/**
 * SettingsScreen — hub unico delle impostazioni (route /impostazioni).
 * Consolida quello che prima era sparso nel menu inbox: notifiche, suono,
 * rilettura, cambio PIN, contatti pre-approvati, installazione, aggiornamenti,
 * svuota dispositivo. Riusa le stesse funzioni di libreria di InboxScreen.
 *
 * UI: sezioni a fisarmonica (card collassabili) con titolo+icona+sottotitolo,
 * chiuse di default → schermata pulita e scansionabile.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn } from '../components/Frame';
import { getVersionLabel } from '../lib/appVersion';
import { Section } from '../components/Section';
import { useStore } from '../lib/store';
import { auth, signOut } from '../lib/firebase';
import { clearSession } from '../lib/storage';
import { wipeKeys } from '../lib/keys';
import { clearLocalBackup, clearLastSigil, resetLocalAttempts } from '../lib/pinCrypto';
import { isNativeApp } from '../lib/platform';
import { isSelfReadable, setSelfReadable } from '../lib/messaging';
import {
  enableNotifications, disableNotifications, areNotificationsEnabled,
  isNotifSoundEnabled, setNotifSoundEnabled, playNotificationSound,
} from '../lib/notifications';

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { reset, uid, premium, isFounder, premiumLifetime } = useStore();
  const hasPremium = premium || isFounder || premiumLifetime;
  const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
  const buildId = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : null;
  const [verLabel, setVerLabel] = useState(`v${version}`);
  useEffect(() => { getVersionLabel().then(setVerLabel).catch(() => {}); }, []);

  const [notifOn, setNotifOn] = useState(() => { try { return areNotificationsEnabled(); } catch { return false; } });
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifMsg, setNotifMsg] = useState(null);
  const [soundOn, setSoundOn] = useState(() => { try { return isNotifSoundEnabled(); } catch { return true; } });
  const [selfRead, setSelfRead] = useState(() => { try { return isSelfReadable(); } catch { return true; } });
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateMsg, setUpdateMsg] = useState(null);

  async function toggleNotif() {
    setNotifBusy(true); setNotifMsg(null);
    try {
      if (notifOn) { await disableNotifications(); setNotifOn(false); }
      else {
        const r = await enableNotifications();
        if (r?.ok) setNotifOn(true);
        else setNotifMsg(`non attivate (${r?.reason || 'errore'}). Trovi i dettagli nel banner in inbox.`);
      }
    } catch (e) { setNotifMsg(`errore: ${e?.message || e}`); }
    finally { setNotifBusy(false); }
  }

  function toggleSound() {
    const v = !soundOn;
    setNotifSoundEnabled(v); setSoundOn(v);
    if (v) { try { playNotificationSound(); } catch {} }
  }

  function toggleSelf() {
    const v = !selfRead;
    setSelfReadable(v); setSelfRead(v);
  }

  async function checkForUpdates() {
    setUpdateBusy(true);
    setUpdateMsg(null);
    try {
      if (!('serviceWorker' in navigator)) {
        setUpdateMsg(`✓ Sei già all'ultima versione (v${version}).`);
        setUpdateBusy(false);
        return;
      }
      const regs = await navigator.serviceWorker.getRegistrations();
      // Avvia il controllo e aspetta (max 5s) che un eventuale nuovo service
      // worker raggiunga lo stato "installed". Se non arriva → già aggiornati.
      const found = await new Promise((resolve) => {
        let settled = false;
        const done = (v) => { if (!settled) { settled = true; resolve(v); } };
        if (regs.length === 0) { done(false); return; }
        for (const reg of regs) {
          if (reg.waiting) done(true);
          reg.addEventListener('updatefound', () => {
            const sw = reg.installing;
            if (!sw) return;
            sw.addEventListener('statechange', () => {
              // "installed" + controller esistente = AGGIORNAMENTO (non 1ª installazione)
              if (sw.state === 'installed' && navigator.serviceWorker.controller) done(true);
            });
          });
          reg.update().catch(() => {});
        }
        setTimeout(() => done(false), 5000);
      });
      if (found) {
        setUpdateMsg('Nuova versione trovata — aggiorno…');
        // In modalità "prompt" il SW nuovo è in attesa: lo attiviamo davvero
        // (SKIP_WAITING + reload) via l'attivatore esposto in main.jsx.
        setTimeout(() => {
          if (typeof window !== 'undefined' && window.__nullumActivateUpdate) window.__nullumActivateUpdate();
          else window.location.reload();
        }, 900);
      } else {
        setUpdateMsg(`✓ Sei già all'ultima versione (v${version}).`);
        setUpdateBusy(false);
      }
    } catch {
      setUpdateMsg('Controllo non riuscito, riprova.');
      setUpdateBusy(false);
    }
  }

  // Cambia/aggiungi identità: blocca la sessione in-memory SENZA cancellare il
  // backup o il catalogo (l'identità resta sbloccabile). Va al selettore, da cui
  // si sceglie un'altra identità o se ne crea una nuova (su invito).
  async function doSwitch() {
    try { await signOut(auth()); } catch {}
    try { clearSession(); } catch {}
    try { await wipeKeys(); } catch {} // chiavi in-memory: si ri-derivano allo sblocco
    reset();
    navigate('/accounts', { replace: true });
  }

  async function doWipe() {
    if (!confirm(
      'Svuotare questo dispositivo?\n\nToglie la tua identità da qui. Rientri con @sigil + PIN o con le 24 parole. Senza, non si torna indietro.'
    )) return;
    try { await signOut(auth()); } catch {}
    try { clearSession(); } catch {}
    try { await wipeKeys(); } catch {}
    try { clearLocalBackup(); } catch {}
    try { clearLastSigil(); } catch {}
    try { resetLocalAttempts(); } catch {}
    reset();
    navigate('/welcome', { replace: true });
  }

  return (
    <Frame statusLeft="IMPOSTAZIONI" statusRight="5G">
      <Header title="// Impostazioni" crumb="◀ indietro" onCrumbClick={() => navigate('/inbox')} />

      <p className="text-[13px] text-ink-dim mb-4">
        Tocca una sezione per aprirla.
      </p>

      <Section icon="🔔" title="Notifiche" subtitle="push e suono">
        <Btn variant="ghost" onClick={toggleNotif} disabled={notifBusy}>
          {notifBusy ? '…' : (notifOn ? '🔕 Disattiva notifiche' : '🔔 Attiva notifiche')}
        </Btn>
        <Btn variant="ghost" onClick={toggleSound}>
          {soundOn ? '🔊 Suono notifiche: ON' : '🔇 Suono notifiche: OFF'}
        </Btn>
        {notifMsg && <p className="text-[13px] text-ink-dim break-words select-text">{notifMsg}</p>}
        <p className="text-[12px] text-ink-dim">
          Ad app aperta suona qui. Ad app chiusa decide il telefono.
        </p>
      </Section>

      <Section icon="🔒" title="Privacy" subtitle="rileggere i tuoi messaggi">
        <Btn variant="ghost" onClick={toggleSelf}>
          {selfRead ? '👁 Rileggi i miei messaggi: ON' : '👁 Rileggi i miei messaggi: OFF'}
        </Btn>
        <p className="text-[12px] text-ink-dim">
          Con OFF, nemmeno tu potrai rileggere ciò che invii.
        </p>
      </Section>

      <Section icon="👤" title="Account e contatti" subtitle="PIN, passphrase e contatti">
        <Btn variant="ghost" onClick={() => navigate('/change-pin')}>⚙ Cambia PIN</Btn>
        {premium && (
          <Btn variant="ghost" onClick={() => navigate('/set-passphrase')}>🔑 Passphrase di sblocco (Premium)</Btn>
        )}
        <Btn variant="ghost" onClick={() => navigate('/whitelist')}>✓ Contatti pre-approvati</Btn>
        <Btn variant="ghost" onClick={doSwitch}>🔄 Cambia / aggiungi identità</Btn>
      </Section>

      <Section icon="📱" title="App" subtitle="installazione, aggiornamenti, versione">
        {!isNativeApp() && (
          <Btn variant="ghost" onClick={() => navigate('/install')}>📲 Come installare sul telefono</Btn>
        )}
        {(hasPremium || isNativeApp()) && (
          <Btn variant="ghost" onClick={() => navigate('/tor')}>🧅 IP anonimo (Tor)</Btn>
        )}
        <Btn variant="ghost" onClick={checkForUpdates} disabled={updateBusy}>
          {updateBusy ? '… controllo in corso' : '🔄 Verifica aggiornamenti'}
        </Btn>
        {updateMsg && (
          <p className={`text-[13px] ${updateMsg.startsWith('✓') ? 'text-ink' : 'text-ink-dim'}`}>{updateMsg}</p>
        )}
        <p className="text-[12px] text-ink-dim">
          versione: <span className="text-ink">{verLabel}</span>
          {buildId ? <span className="text-ink-faint"> · web {buildId}</span> : null}
        </p>
      </Section>

      <Section icon="⚠️" title="Zona pericolosa" subtitle="rimuovi l'identità da questo device" tone="danger">
        <Btn variant="danger" onClick={doWipe}>🗑 Svuota questo dispositivo</Btn>
        <p className="text-[12px] text-ink-dim">
          Toglie la tua identità da qui. Rientri con @sigil + PIN o con le 24 parole.
        </p>
        <p className="text-[11px] text-ink-dim break-all select-all">uid (diagnostico): {uid || '?'}</p>
      </Section>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
      </Footer>
    </Frame>
  );
}
